import type { Ingredient, LunchComponent, Meal } from '../domain/types.js';
import type { Lookups } from '../domain/requirements.js';
import { INGREDIENTS } from './ingredients.js';
import { LUNCH_COMPONENTS } from './lunchComponents.js';
import { MEALS } from './meals.js';

export { HOUSEHOLD } from './household.js';
export { INGREDIENTS } from './ingredients.js';
export { LUNCH_COMPONENTS } from './lunchComponents.js';
export { PACK_SIZES } from './packSizes.js';
export { MEALS } from './meals.js';

export const INGREDIENTS_BY_ID: ReadonlyMap<string, Ingredient> = new Map(
  INGREDIENTS.map((ingredient) => [ingredient.id, ingredient]),
);

export const MEALS_BY_ID: ReadonlyMap<string, Meal> = new Map(
  MEALS.map((meal) => [meal.id, meal]),
);

export const LUNCH_COMPONENTS_BY_ID: ReadonlyMap<string, LunchComponent> = new Map(
  LUNCH_COMPONENTS.map((component) => [component.id, component]),
);

export const LOOKUPS: Lookups = {
  ingredients: INGREDIENTS_BY_ID,
  meals: MEALS_BY_ID,
  lunchComponents: LUNCH_COMPONENTS_BY_ID,
};

/** Lowercase, trimmed, punctuation-light, single-spaced. */
function normalise(value: string): string {
  return value.toLowerCase().trim().replace(/[.,]/g, '').replace(/\s+/g, ' ');
}

const BY_NAME: ReadonlyMap<string, Ingredient> = new Map(
  INGREDIENTS.flatMap((ingredient) => [
    [normalise(ingredient.id.replace(/-/g, ' ')), ingredient] as const,
    [normalise(ingredient.id), ingredient] as const,
    [normalise(ingredient.name), ingredient] as const,
    ...ingredient.aliases.map((alias) => [normalise(alias), ingredient] as const),
  ]),
);

/**
 * Map a free-text ingredient name onto a canonical record.
 *
 * Returns null rather than inventing a record. Uncontrolled ingredient creation
 * is the fastest way to destroy shopping-list aggregation — "mince" and
 * "beef mince" as two rows means buying mince twice.
 */
export function resolveIngredientName(name: string): Ingredient | null {
  const key = normalise(name);
  if (key.length === 0) return null;
  return BY_NAME.get(key) ?? null;
}

/**
 * Type-ahead for the recipe form. An exact name or alias match comes first
 * (so "mince" is beef mince, not mincemeat), then names that start with the
 * query, then names or aliases that contain it.
 */
export function searchIngredients(query: string, limit = 6): Ingredient[] {
  const q = normalise(query);
  if (q.length === 0) return [];
  const out: Ingredient[] = [];
  const add = (ingredient: Ingredient | undefined | null): void => {
    if (ingredient && !out.some((i) => i.id === ingredient.id)) out.push(ingredient);
  };
  add(BY_NAME.get(q));
  for (const i of INGREDIENTS) if (normalise(i.name).startsWith(q)) add(i);
  for (const i of INGREDIENTS) {
    if (normalise(i.name).includes(q) || i.aliases.some((a) => normalise(a).includes(q))) add(i);
  }
  return out.slice(0, limit);
}
